<template>
    <b-modal
        :visible="value"
        :title="mode === 'create' ? '수집 설정 추가' : '수집 설정 수정'"
        size="lg"
        hide-footer
        scrollable
        @input="$emit('input', $event)"
        @hide="$emit('input', false)"
        class="activity-config-form-modal"
    >
        <div class="acf-body">
            <!-- 1. 기본 설정 -->
            <div class="acf-group">
                <div class="acf-group-label">
                    기본 설정
                    <span v-if="mode === 'edit'" class="acf-group-badge">조직·시설·카테고리 수정 불가</span>
                </div>
                <div class="acf-row acf-row--3">
                    <div class="acf-field">
                        <label class="acf-label">조직 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="localForm.orgIdx == null ? '' : localForm.orgIdx"
                            :options="orgSelectOptions"
                            placeholder="조직 선택"
                            :placeholderDisabled="false"
                            :disabled="mode === 'edit'"
                            @input="(v) => onFormChange('orgIdx', v === '' ? null : Number(v))"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">시설</label>
                        <CarbonSelect
                            :value="localForm.facilityIdx == null ? '' : localForm.facilityIdx"
                            :options="facilitySelectOptions"
                            placeholder="전체 시설"
                            :placeholderDisabled="false"
                            :disabled="mode === 'edit'"
                            @input="(v) => onFormChange('facilityIdx', v === '' ? null : Number(v))"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">카테고리 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="localForm.categoryIdx == null ? '' : localForm.categoryIdx"
                            :options="categorySelectOptions"
                            placeholder="카테고리 선택"
                            :placeholderDisabled="false"
                            :disabled="mode === 'edit'"
                            @input="(v) => onFormChange('categoryIdx', v === '' ? null : Number(v))"
                        />
                    </div>
                </div>
            </div>

            <!-- 2. 유효 기간 -->
            <div class="acf-group">
                <div class="acf-group-label">유효 기간</div>
                <div class="acf-row acf-row--2">
                    <div class="acf-field">
                        <label class="acf-label">시작월 <span class="acf-required">*</span></label>
                        <DatePicker
                            :value="localForm.startMonth"
                            type="month"
                            format="YYYYMM"
                            placeholder="YYYYMM"
                            value-type="format"
                            @input="onFormChange('startMonth', $event)"
                        />
                        <span class="acf-hint">설정이 유효하기 시작하는 월</span>
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">
                            종료월
                            <span class="acf-optional">(선택)</span>
                        </label>
                        <div class="acf-inline">
                            <DatePicker
                                :value="localForm.endMonth"
                                type="month"
                                format="YYYYMM"
                                placeholder="미입력 시 무기한"
                                value-type="format"
                                @input="onFormChange('endMonth', $event)"
                            />
                            <button
                                v-if="localForm.endMonth"
                                type="button"
                                class="acf-clear-btn"
                                title="종료월 지우기"
                                @click="onFormChange('endMonth', null)"
                            >
                                &times;
                            </button>
                        </div>
                        <span class="acf-hint">미입력 시 무기한 유효</span>
                    </div>
                </div>
            </div>

            <!-- 3. 수집 방식 -->
            <div class="acf-group">
                <div class="acf-group-label">수집 방식</div>
                <div class="acf-row acf-row--3">
                    <div class="acf-field">
                        <label class="acf-label">수집 방법 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="localForm.collectionMethod"
                            :options="collectionMethods"
                            @input="(v) => onFormChange('collectionMethod', v)"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">데이터 출처</label>
                        <CarbonSelect
                            :value="localForm.dataSource == null ? '' : localForm.dataSource"
                            :options="dataSourceOptions"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('dataSource', v === '' ? null : v)"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">보고 주기 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="localForm.reportCycle == null ? '' : localForm.reportCycle"
                            :options="reportCycleOptions"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('reportCycle', v === '' ? null : v)"
                        />
                    </div>
                </div>
            </div>

            <!-- 3b. 시스템 연동 — 관제점 (CxEMS 자동 수집용 apiParams) -->
            <div v-if="localForm.collectionMethod === 'SYSTEM'" class="acf-group">
                <div class="acf-group-label">시스템 연동</div>
                <div class="acf-row acf-row--1">
                    <div class="acf-field">
                        <label class="acf-label">관제점 선택 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="selectedControlPointValue"
                            :options="controlPointSelectOptions"
                            placeholder="관제점 선택"
                            :placeholderDisabled="false"
                            :disabled="loadingPoints"
                            @input="onControlPointSelect"
                        />
                        <span v-if="loadingPoints" class="acf-hint">관제점 목록을 불러오는 중입니다.</span>
                        <span v-else-if="!controlPointSelectOptions.length" class="acf-hint">
                            ENSPLY 그룹 관제점이 없습니다. 조건(isVirtual=1, ptGroup=ENSPLY)을 확인하세요.
                        </span>
                    </div>
                </div>
            </div>

            <!-- 3c. 자동 수집 (SYSTEM 전용) -->
            <div v-if="isSystemMethod" class="acf-group">
                <div class="acf-group-label">자동 수집</div>
                <div class="acf-row acf-row--3">
                    <div class="acf-field acf-field--check">
                        <label class="acf-label">자동 수집 사용</label>
                        <label class="acf-checkbox">
                            <input type="checkbox" :checked="true" disabled />
                            <span>자동 수집 사용</span>
                        </label>
                        <span class="acf-hint">SYSTEM 연동일 때만 동작합니다.</span>
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">수집 주기</label>
                        <CarbonSelect
                            :value="localForm.collectSchedule == null ? '' : localForm.collectSchedule"
                            :options="collectScheduleOptions"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('collectSchedule', v === '' ? null : v)"
                        />
                    </div>
                    <div class="acf-field" v-if="localForm.collectSchedule === 'MONTHLY'">
                        <label class="acf-label">수집일 <span class="acf-required">*</span></label>
                        <input
                            class="acf-text-input"
                            type="number"
                            min="1"
                            max="31"
                            :value="localForm.collectDay == null ? '' : localForm.collectDay"
                            placeholder="1~31"
                            @input="
                                onFormChange(
                                    'collectDay',
                                    $event.target.value === '' ? null : Number($event.target.value),
                                )
                            "
                        />
                        <span class="acf-hint">매월 해당 일에 수집 (미설정 시 기본값은 5일)</span>
                    </div>
                    <div class="acf-field" v-if="localForm.collectSchedule === 'DAILY'">
                        <label class="acf-label">수집 시간 <span class="acf-optional"></span></label>
                        <DatePicker
                            :value="localForm.collectTime"
                            type="time"
                            format="HH:mm"
                            value-type="format"
                            placeholder="HH:mm"
                            @input="onFormChange('collectTime', $event)"
                        />
                    </div>
                </div>
            </div>

            <!-- 4. 배출원·단위 -->
            <div class="acf-group">
                <div class="acf-group-label">배출원 · 단위</div>
                <div class="acf-row acf-row--4">
                    <div class="acf-field">
                        <label class="acf-label">연료</label>
                        <CarbonSelect
                            :value="localForm.preferredFuel == null ? '' : localForm.preferredFuel"
                            :options="fuelTypes"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('preferredFuel', v === '' ? null : v)"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">단위</label>
                        <CarbonSelect
                            :value="localForm.preferredUnit == null ? '' : localForm.preferredUnit"
                            :options="activityUnitOptionsForSelect"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('preferredUnit', v === '' ? null : v)"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">배출 계수</label>
                        <CarbonSelect
                            :value="localForm.factorIdx == null ? '' : localForm.factorIdx"
                            :options="factorSelectOptions"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('factorIdx', v === '' ? null : Number(v))"
                        />
                    </div>
                    <div class="acf-field">
                        <label class="acf-label">산정등급</label>
                        <CarbonSelect
                            :value="localForm.tier == null ? '' : localForm.tier"
                            :options="tierOptionsForSelect"
                            placeholder="선택"
                            :placeholderDisabled="false"
                            @input="(v) => onFormChange('tier', v === '' ? null : v)"
                        />
                        <span class="acf-hint">TIER1~4 우선조건</span>
                    </div>
                </div>
            </div>

            <!-- 5. 상태 -->
            <div class="acf-group acf-group--last">
                <div class="acf-group-label">상태</div>
                <div class="acf-row acf-row--2">
                    <div class="acf-field">
                        <label class="acf-label">상태 <span class="acf-required">*</span></label>
                        <CarbonSelect
                            :value="localForm.draftStat"
                            :options="draftStatOptions"
                            @input="(v) => onFormChange('draftStat', v)"
                        />
                    </div>
                    <div class="acf-field acf-field--check">
                        <label class="acf-label">필수 보고</label>
                        <label class="acf-checkbox">
                            <input
                                type="checkbox"
                                :checked="localForm.isRequired === 1"
                                @change="onFormChange('isRequired', $event.target.checked ? 1 : 0)"
                            />
                            <span>필수로 보고</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="acf-footer">
            <CarbonButton @click="$emit('input', false)">취소</CarbonButton>
            <CarbonButton variant="primary" :disabled="!canSaveConfig" @click="onSave">저장</CarbonButton>
        </div>
    </b-modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonCodes from "../../../codes";
import backEndApi from "@/api/backEndApi";
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";

/** CxEMS 자동 수집 — getPointInfoByGroup 조회 조건 (백엔드 API와 동일) */
const SYSTEM_POINT_LIST_QUERY = Object.freeze({
    isVirtual: 1,
    ptGroup: "ENSPLY",
});

const defaultForm = () => ({
    orgIdx: null,
    facilityIdx: null,
    categoryIdx: null,
    startMonth: null,
    endMonth: null,
    draftStat: "DRAFT",
    isRequired: 0,
    collectionMethod: "MANUAL",
    dataSource: null,
    reportCycle: "MONTH",
    preferredFuel: null,
    preferredUnit: null,
    factorIdx: null,
    tier: null,
    isAutoCollect: 0,
    collectSchedule: null,
    collectDay: null,
    collectTime: null,
    apiParams: null,
});

export default {
    name: "ActivityConfigFormModal",
    components: { DatePicker, CarbonButton, CarbonSelect },
    props: {
        value: { type: Boolean, default: false },
        mode: { type: String, default: "create" },
        form: { type: Object, default: defaultForm },
        organizations: { type: Array, default: () => [] },
        facilities: { type: Array, default: () => [] },
        categories: { type: Array, default: () => [] },
        factors: { type: Array, default: () => [] },
    },
    data() {
        return {
            localForm: { ...defaultForm(), ...this.form },
            allPoints: [],
            loadingPoints: false,
        };
    },
    computed: {
        isSystemMethod() {
            return this.localForm.collectionMethod === "SYSTEM" || this.localForm.dataSource === "CXEMS";
        },
        canSaveConfig() {
            const rc = this.localForm.reportCycle;
            const basicValid =
                this.localForm.orgIdx &&
                this.localForm.categoryIdx &&
                this.localForm.startMonth &&
                this.localForm.draftStat &&
                this.localForm.collectionMethod &&
                rc != null &&
                String(rc).trim() !== "";

            if (this.localForm.isAutoCollect === 1) {
                if (!this.localForm.collectSchedule) return false;
                if (this.localForm.collectSchedule === "MONTHLY" && !this.localForm.collectDay) return false;
                if (this.isSystemMethod) {
                    const ap = this.localForm.apiParams;
                    const hasAddr =
                        (Array.isArray(ap?.ptAddrs) && ap.ptAddrs.length > 0) ||
                        (ap?.ptAddr != null && String(ap.ptAddr).trim() !== "");
                    const hasIdx =
                        (Array.isArray(ap?.ptIdxs) && ap.ptIdxs.length > 0) ||
                        (ap?.ptIdx != null && ap.ptIdx !== "" && Number.isFinite(Number(ap.ptIdx)));
                    if (!hasAddr || !hasIdx) return false;
                }
            }
            return basicValid;
        },
        /** 관제점 선택 값 — CarbonSelect는 문자열 value 사용 */
        selectedControlPointValue() {
            const ap = this.localForm.apiParams;
            if (!ap || ap.ptIdx == null || ap.ptIdx === "") return "";
            return String(ap.ptIdx);
        },
        controlPointSelectOptions() {
            return (this.allPoints || []).map((p) => {
                const idx = p.ptIdx;
                const addr = p.ptAddr || p.pointAddress || "";
                const name = p.ptName || p.pointName || "";
                const label = [name, addr].filter(Boolean).join(" · ") || String(idx);
                return { value: idx != null ? String(idx) : "", text: label };
            });
        },
        collectionMethods() {
            return CarbonCodes.collectionMethod.getList();
        },
        dataSourceOptions() {
            return CarbonCodes.dataSource.getList();
        },
        reportCycleOptions() {
            return CarbonCodes.reportCycle.getList();
        },
        fuelTypes() {
            return CarbonCodes.fuelType.getList();
        },
        draftStatOptions() {
            return CarbonCodes.draftStat.getList();
        },
        collectScheduleOptions() {
            return CarbonCodes.collectSchedule.getList();
        },
        activityUnitOptionsForSelect() {
            const list = CarbonCodes.activityUnit.getList();
            return list.map((o) => ({ value: o.value, text: o.value }));
        },
        tierOptionsForSelect() {
            const list = CarbonCodes.tier ? CarbonCodes.tier.getList() : [];
            if (list.length) return list.map((o) => ({ value: o.value, text: o.text || o.value }));
            return [
                { value: "TIER1", text: "1급" },
                { value: "TIER2", text: "2급" },
                { value: "TIER3", text: "3급" },
                { value: "TIER4", text: "4급" },
            ];
        },
        orgSelectOptions() {
            return this.organizations.map((o) => ({ value: o.orgIdx, text: o.orgName }));
        },
        facilitySelectOptions() {
            return this.facilities.map((f) => ({
                value: f.facilityIdx || f.orgFacilityIdx,
                text: f.facilityName,
            }));
        },
        categorySelectOptions() {
            return this.categories.map((c) => ({ value: c.categoryIdx, text: c.categoryName }));
        },
        factorSelectOptions() {
            return this.factors.map((f) => ({ value: f.factorIdx, text: f.factorCode }));
        },
    },
    watch: {
        value(visible) {
            if (visible) {
                this.localForm = { ...defaultForm(), ...this.form };
                this.loadPoints();
            }
        },
        form: {
            handler(v) {
                if (v && this.value) {
                    this.localForm = { ...defaultForm(), ...v };
                }
            },
            deep: true,
        },
        "localForm.collectionMethod"(newVal) {
            if (newVal === "SYSTEM") {
                this.localForm = { ...this.localForm, isAutoCollect: 1 };
                this.loadPoints();
            } else if (this.localForm.dataSource !== "CXEMS") {
                this.localForm = { ...this.localForm, isAutoCollect: 0 };
            }
            this.$emit("update:form", { ...this.localForm });
        },
        "localForm.dataSource"(newVal) {
            if (newVal === "CXEMS") {
                this.localForm = { ...this.localForm, isAutoCollect: 1 };
            } else if (this.localForm.collectionMethod !== "SYSTEM") {
                this.localForm = { ...this.localForm, isAutoCollect: 0 };
            }
            this.$emit("update:form", { ...this.localForm });
        },
    },
    methods: {
        onFormChange(field, value) {
            this.localForm = { ...this.localForm, [field]: value };
            this.$emit("update:form", { ...this.localForm });
        },
        /**
         * 관제점 선택 시 apiParams에 ptAddr·ptIdx 단일값과 ptAddrs·ptIdxs 배열을 함께 설정
         * (carbon ActivityDataCollectorWorker 가 단일/배열 별칭 모두 지원)
         */
        onControlPointSelect(value) {
            if (value === "" || value == null) {
                this.onFormChange("apiParams", null);
                return;
            }
            const ptIdx = Number(value);
            const point = (this.allPoints || []).find((p) => Number(p.ptIdx) === ptIdx);
            if (!point) {
                this.onFormChange("apiParams", null);
                return;
            }
            const ptAddr = String(point.ptAddr || point.pointAddress || "").trim();
            const apiParams = {
                ptAddr,
                ptIdx,
                ptAddrs: ptAddr ? [ptAddr] : [],
                ptIdxs: Number.isFinite(ptIdx) && ptIdx > 0 ? [ptIdx] : [],
            };
            this.onFormChange("apiParams", apiParams);
        },
        async loadPoints() {
            if (this.localForm.collectionMethod !== "SYSTEM") {
                this.allPoints = [];
                return;
            }
            this.loadingPoints = true;
            try {
                const result = await backEndApi.points.getPointInfoByGroup(SYSTEM_POINT_LIST_QUERY, 0, 50);
                const raw = result?.data;
                const rows = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
                this.allPoints = rows;
            } catch (err) {
                console.warn("[ActivityConfigFormModal] 관제점 목록 로드 실패:", err);
                this.allPoints = [];
            } finally {
                this.loadingPoints = false;
            }
            if (this.localForm.collectionMethod === "SYSTEM") {
                this._enrichApiParamsFromLoadedPoints();
            }
        },
        /**
         * 기존 설정(apiParams에 주소만 있거나 idx만 있는 경우)을 목록과 맞춰 ptAddr·ptIdx를 보강
         */
        _enrichApiParamsFromLoadedPoints() {
            const ap = this.localForm.apiParams;
            if (!ap || !this.allPoints?.length) return;
            const hasIdx = ap.ptIdx != null && ap.ptIdx !== "" && Number.isFinite(Number(ap.ptIdx));
            const addrStr = ap.ptAddr || (Array.isArray(ap.ptAddrs) && ap.ptAddrs[0]) || "";
            const hasAddr = addrStr != null && String(addrStr).trim() !== "";
            if (hasIdx && hasAddr) return;

            let found = null;
            if (hasIdx && !hasAddr) {
                found = this.allPoints.find((p) => Number(p.ptIdx) === Number(ap.ptIdx));
            } else if (hasAddr && !hasIdx) {
                const key = String(addrStr).trim();
                found = this.allPoints.find((p) => String(p.ptAddr || p.pointAddress || "").trim() === key);
            }
            if (!found) return;

            const ptAddr = String(found.ptAddr || found.pointAddress || "").trim();
            const ptIdx = Number(found.ptIdx);
            this.localForm = {
                ...this.localForm,
                apiParams: {
                    ...ap,
                    ptAddr,
                    ptIdx,
                    ptAddrs: ptAddr ? [ptAddr] : [],
                    ptIdxs: Number.isFinite(ptIdx) && ptIdx > 0 ? [ptIdx] : [],
                },
            };
            this.$emit("update:form", { ...this.localForm });
        },
        onSlideEnter(el) {
            el.style.overflow = "hidden";
        },
        onSlideAfterEnter(el) {
            el.style.overflow = "visible";
        },
        onSlideLeave(el) {
            el.style.overflow = "hidden";
        },
        /**
         * 탄소 플러그인 activityConfig.validators.js (create/update) 기준 YYYYMM 정규화
         */
        toYYYYMM(v) {
            if (v == null || v === "") return null;
            const s = String(v).trim();
            if (/^\d{6}$/.test(s)) return s;
            const m = s.match(/^(\d{4})-(\d{1,2})$/);
            return m ? `${m[1]}${m[2].padStart(2, "0")}` : null;
        },
        _optionValueSet(list) {
            return new Set((list || []).map((o) => o && o.value).filter((v) => v !== undefined && v !== null));
        },
        /**
         * POST/PUT 바디 규정에 맞게 검증·정규화 (Joi createConfigSchema / updateConfigSchema 정합)
         */
        validateAndSanitizePayloadForApi() {
            const f = this.localForm;
            const errors = [];

            const orgIdx = f.orgIdx != null && f.orgIdx !== "" ? Number(f.orgIdx) : null;
            if (!orgIdx || orgIdx <= 0 || !Number.isInteger(orgIdx)) {
                errors.push("조직을 선택하세요.");
            }

            let facilityIdx = f.facilityIdx != null && f.facilityIdx !== "" ? Number(f.facilityIdx) : null;
            if (facilityIdx != null && (!Number.isInteger(facilityIdx) || facilityIdx <= 0)) {
                errors.push("시설 값이 올바르지 않습니다.");
                facilityIdx = null;
            }

            const categoryIdx = f.categoryIdx != null && f.categoryIdx !== "" ? Number(f.categoryIdx) : null;
            if (!categoryIdx || categoryIdx <= 0 || !Number.isInteger(categoryIdx)) {
                errors.push("카테고리를 선택하세요.");
            }

            const startMonth = this.toYYYYMM(f.startMonth);
            if (!startMonth) {
                errors.push("시작월은 YYYYMM 형식이어야 합니다.");
            }

            const endMonth = this.toYYYYMM(f.endMonth);
            if (f.endMonth && String(f.endMonth).trim() !== "" && !endMonth) {
                errors.push("종료월은 YYYYMM 형식이어야 합니다.");
            }
            if (startMonth && endMonth && startMonth > endMonth) {
                errors.push("시작월은 종료월보다 늦을 수 없습니다.");
            }

            const draftStat = f.draftStat;
            if (!draftStat || !this._optionValueSet(this.draftStatOptions).has(draftStat)) {
                errors.push("상태를 올바르게 선택하세요.");
            }

            const collectionMethod = f.collectionMethod;
            if (!collectionMethod || !this._optionValueSet(this.collectionMethods).has(collectionMethod)) {
                errors.push("수집 방법을 올바르게 선택하세요.");
            }

            let reportCycle = f.reportCycle;
            if (reportCycle === "" || reportCycle == null) {
                reportCycle = "MONTH";
            }
            if (!this._optionValueSet(this.reportCycleOptions).has(reportCycle)) {
                errors.push("보고 주기는 MONTH, QUARTER, HALF, YEAR 중 하나여야 합니다.");
            }

            let dataSource = f.dataSource;
            if (dataSource === "" || dataSource === undefined) {
                dataSource = null;
            } else {
                dataSource = String(dataSource).trim();
                if (dataSource.length > 100) {
                    errors.push("데이터 출처는 100자 이내여야 합니다.");
                }
            }

            let preferredFuel = f.preferredFuel;
            if (preferredFuel === "" || preferredFuel == null) {
                preferredFuel = null;
            } else {
                preferredFuel = String(preferredFuel).trim();
                if (preferredFuel.length > 50) {
                    errors.push("연료는 50자 이내여야 합니다.");
                }
            }

            let preferredUnit = f.preferredUnit;
            if (preferredUnit === "" || preferredUnit == null) {
                preferredUnit = null;
            } else {
                const unitList = CarbonCodes.activityUnit.getList() || [];
                const u = String(preferredUnit).trim();
                const match = unitList.find((o) => String(o.value).toUpperCase() === u.toUpperCase());
                if (!match) {
                    errors.push("단위는 활동 단위(ACTIVITY_UNIT) 공통코드에 등록된 값만 선택할 수 있습니다.");
                } else {
                    preferredUnit = match.value;
                }
            }

            let factorIdx = f.factorIdx != null && f.factorIdx !== "" ? Number(f.factorIdx) : null;
            if (factorIdx != null && (!Number.isInteger(factorIdx) || factorIdx <= 0)) {
                errors.push("배출 계수 선택값이 올바르지 않습니다.");
                factorIdx = null;
            }

            let tier = f.tier;
            if (tier === "" || tier == null) {
                tier = null;
            } else if (!this._optionValueSet(this.tierOptionsForSelect).has(tier)) {
                errors.push("산정등급을 올바르게 선택하세요.");
            }

            const isRequired = f.isRequired === 1 ? 1 : 0;
            const isAutoCollect = f.isAutoCollect === 1 ? 1 : 0;

            const timeRe = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
            let collectSchedule = f.collectSchedule;
            if (collectSchedule === "" || collectSchedule == null) {
                collectSchedule = null;
            }

            let collectDay = f.collectDay != null && f.collectDay !== "" ? parseInt(f.collectDay, 10) : null;
            if (Number.isNaN(collectDay)) {
                collectDay = null;
            }

            let collectTime =
                f.collectTime != null && String(f.collectTime).trim() !== "" ? String(f.collectTime).trim() : null;

            if (isAutoCollect === 1) {
                const ccSet = this._optionValueSet(this.collectScheduleOptions);
                if (!collectSchedule || !ccSet.has(collectSchedule)) {
                    errors.push("자동 수집 시 수집 주기(수집 사이클)를 선택하세요.");
                }
                if (collectSchedule === "MONTHLY") {
                    if (collectDay == null || collectDay < 1 || collectDay > 31) {
                        errors.push("월 단위 수집 시 수집일(1–31)을 입력하세요.");
                    }
                    // MONTHLY는 UI에서 수집 시간을 받지 않음 (백엔드 워커도 사용하지 않음)
                    collectTime = null;
                }
                // collectTime은 백엔드 자동수집 워커에서 사용되지 않으므로 선택 입력으로 둔다.
                if (collectTime && !timeRe.test(collectTime)) {
                    errors.push("수집 시간은 HH:mm 형식이어야 합니다.");
                }
                if (this.isSystemMethod) {
                    const ap = f.apiParams;
                    const hasAddr =
                        (Array.isArray(ap?.ptAddrs) && ap.ptAddrs.length > 0) ||
                        (ap?.ptAddr != null && String(ap.ptAddr).trim() !== "");
                    const hasIdx =
                        (Array.isArray(ap?.ptIdxs) && ap.ptIdxs.length > 0) ||
                        (ap?.ptIdx != null && ap.ptIdx !== "" && Number.isFinite(Number(ap.ptIdx)));
                    if (!hasAddr || !hasIdx) {
                        errors.push("시스템 연동 시 관제점(ptAddr·ptIdx)을 선택하세요.");
                    }
                }
            }

            let apiParams = f.apiParams || null;
            if (apiParams && typeof apiParams === "object") {
                const ptAddrRaw = apiParams.ptAddr;
                const ptIdxRaw = apiParams.ptIdx;
                const ptAddrsFrom =
                    Array.isArray(apiParams.ptAddrs) && apiParams.ptAddrs.length
                        ? apiParams.ptAddrs.map((a) => String(a).trim()).filter(Boolean)
                        : ptAddrRaw != null && String(ptAddrRaw).trim() !== ""
                          ? [String(ptAddrRaw).trim()]
                          : [];
                const ptIdxsFrom =
                    Array.isArray(apiParams.ptIdxs) && apiParams.ptIdxs.length
                        ? apiParams.ptIdxs.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
                        : ptIdxRaw != null && ptIdxRaw !== "" && Number.isFinite(Number(ptIdxRaw))
                          ? [Number(ptIdxRaw)]
                          : [];
                const ptAddr =
                    ptAddrRaw != null && String(ptAddrRaw).trim() !== ""
                        ? String(ptAddrRaw).trim()
                        : (ptAddrsFrom[0] ?? null);
                const ptIdx =
                    ptIdxRaw != null && ptIdxRaw !== "" && Number.isFinite(Number(ptIdxRaw))
                        ? Number(ptIdxRaw)
                        : (ptIdxsFrom[0] ?? null);
                apiParams = {
                    ...apiParams,
                    ptAddr,
                    ptIdx,
                    ptAddrs: ptAddrsFrom,
                    ptIdxs: ptIdxsFrom,
                };
            }

            const payload = {
                ...(this.mode === "edit" && f.configIdx != null ? { configIdx: f.configIdx } : {}),
                orgIdx,
                facilityIdx,
                categoryIdx,
                factorIdx,
                startMonth,
                endMonth,
                draftStat,
                isRequired,
                collectionMethod,
                dataSource,
                isAutoCollect,
                collectSchedule,
                collectDay,
                collectTime,
                apiParams,
                preferredFuel,
                preferredUnit,
                tier,
                reportCycle,
            };

            return { errors, payload };
        },
        onSave() {
            const { errors, payload } = this.validateAndSanitizePayloadForApi();
            if (errors.length) {
                this.$bvToast.toast(errors.join(" "), {
                    title: "입력 확인",
                    variant: "warning",
                    solid: true,
                });
                return;
            }
            this.$emit("save", payload);
        },
    },
};
</script>

<style scoped>
/* ── 수집 설정 폼 (acf-* prefix) ──────────────────────────── */

.acf-body {
    display: flex;
    flex-direction: column;
    padding: 0;
}

/* 그룹 */
.acf-group {
    padding: 18px 0;
    border-bottom: 1px solid #f1f5f9;
}
.acf-group--last {
    border-bottom: none;
    padding-bottom: 6px;
}

/* 그룹 레이블 */
.acf-group-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 12px;
}
.acf-group-label-sub {
    font-weight: 400;
    color: #cbd5e1;
    text-transform: none;
    letter-spacing: 0;
    font-size: 10px;
}
.acf-group-badge {
    font-size: 10px;
    font-weight: 500;
    color: #94a3b8;
    background: #f1f5f9;
    border-radius: 4px;
    padding: 1px 6px;
    text-transform: none;
    letter-spacing: 0;
}

/* 필드 행 */
.acf-row {
    display: grid;
    gap: 14px;
}
.acf-row--2 {
    grid-template-columns: repeat(2, 1fr);
}
.acf-row--3 {
    grid-template-columns: repeat(3, 1fr);
}
.acf-row--4 {
    grid-template-columns: repeat(4, 1fr);
}
.acf-row--1 {
    grid-template-columns: 1fr;
}

/* 개별 필드 */
.acf-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}
.acf-field--check {
    justify-content: flex-start;
}

/* 라벨 */
.acf-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 3px;
}
.acf-required {
    color: #ef4444;
}
.acf-optional {
    font-weight: 400;
    color: #94a3b8;
    font-size: 12px;
}

/* 힌트 */
.acf-hint {
    font-size: 10px;
    color: #94a3b8;
    margin: 0;
}

/* 인라인 (DatePicker + 지우기 버튼) */
.acf-inline {
    display: flex;
    align-items: center;
    gap: 6px;
}
.acf-inline :deep(.mx-datepicker) {
    flex: 1;
}
.acf-clear-btn {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    border: none;
    background: #f1f5f9;
    border-radius: 50%;
    color: #64748b;
    font-size: 15px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
}
.acf-clear-btn:hover {
    background: #e2e8f0;
    color: #334155;
}

/* 체크박스 */
.acf-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    font-size: 13px;
    color: #334155;
    margin: 0;
    user-select: none;
}
.acf-checkbox input[type="checkbox"] {
    width: 15px;
    height: 15px;
    accent-color: #52b788;
    cursor: pointer;
    margin: 0;
}

/* 푸터 */
.acf-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 0 0;
    border-top: 1px solid #f1f5f9;
    margin-top: 4px;
}

/* DatePicker 그린 테마 통일 */
.acf-body :deep(.mx-datepicker) {
    width: 100%;
}
.acf-body :deep(.mx-input) {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    box-shadow: none;
    height: 34px;
    font-size: 13px;
    color: #0f172a;
}
.acf-body :deep(.mx-input:hover),
.acf-body :deep(.mx-input:focus) {
    border-color: #e2e8f0;
    box-shadow: none;
    outline: none;
}

.acf-text-input {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    box-shadow: none;
    height: 34px;
    font-size: 13px;
    color: #0f172a;
    padding: 0 10px;
    outline: none;
}
.acf-text-input:disabled {
    background: #f8fafc;
    color: #94a3b8;
}
.acf-body :deep(.mx-btn:hover) {
    border-color: #52b788 !important;
    color: #52b788 !important;
    background-color: transparent !important;
}
.acf-body :deep(.mx-calendar-content .cell:hover) {
    background-color: rgba(82, 183, 136, 0.14) !important;
    color: #2f855a !important;
}
.acf-body :deep(.mx-calendar-content .cell.active) {
    background-color: #52b788 !important;
    color: #fff !important;
}
.acf-body :deep(.mx-table-date .today) {
    color: #52b788;
}

@media (max-width: 768px) {
    .acf-row--2,
    .acf-row--3,
    .acf-row--4 {
        grid-template-columns: 1fr;
    }
}
</style>
